/* GLIDE in the browser: a playable sketch for the landing pages.
 *
 * Mirrors the instrument's own behaviour, from docs/manual.md in the firmware
 * repo, as closely as a browser can:
 *   - the keymap: 1..0 / q..p / a..; are three strings, z../ is the jam row
 *   - scale lock, degree-mapped, A minor pentatonic out of the box
 *   - each row is a string: press another key on the same row while holding
 *     one and the voice glides there; let go and it glides back
 *   - the jam row latches an in-key triad under you; another pad slides the
 *     chord, the same pad again lets it go
 *   - LISTEN goes quiet, hears the song, names its key, retunes the keys and
 *     syncs the echo to the tempo; a silent room says NO SIGNAL and changes
 *     nothing
 *   - the pitch trail display
 *
 * The "song" is generated here, in a random key the default scale clashes
 * with. The key detection is real: a chromagram from an AnalyserNode on the
 * song's bus, scored against the Krumhansl-Kessler key profiles.
 *
 * One file serves every [data-lp-play] section on a page.
 */
(function () {
  'use strict';

  var NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
  var MINOR = [0, 2, 3, 5, 7, 8, 10];
  var MAJOR = [0, 2, 4, 5, 7, 9, 11];
  var SCALES = {
    minpent: { name: 'MINOR PENT', steps: [0, 3, 5, 7, 10], parent: MINOR },
    minor: { name: 'MINOR', steps: MINOR, parent: MINOR },
    major: { name: 'MAJOR', steps: MAJOR, parent: MAJOR },
    chromatic: { name: 'NO SCALE LOCK', steps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], parent: MINOR }
  };
  // bottom solo row first (string 1), top row last (string 3)
  var ROWS = [
    { codes: ['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyL', 'Semicolon'], label: 'asdfghjkl;' },
    { codes: ['KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyT', 'KeyY', 'KeyU', 'KeyI', 'KeyO', 'KeyP'], label: 'qwertyuiop' },
    { codes: ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Digit0'], label: '1234567890' }
  ];
  var JAM = { codes: ['KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB', 'KeyN', 'KeyM', 'Comma', 'Period', 'Slash'], label: 'zxcvbnm,./' };
  // keys the default A minor pentatonic clashes with, so the problem is audible
  var SONG_KEYS = [6, 1, 3, 11, 8, 10]; // F#, C#, Eb, B, Ab, Bb (minor)
  var KK_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
  var KK_MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
  var GLIDE_TAU = 0.045;

  function mtof(m) { return 440 * Math.pow(2, (m - 69) / 12); }
  function noteName(m) { return NAMES[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1); }
  function pcName(pc) { return NAMES[((pc % 12) + 12) % 12]; }
  function now(ctx) { return ctx.currentTime; }

  /* ------------------------------------------------------------------ audio */

  function Engine() {
    this.ctx = null;
    this.bpm = 96;
  }

  Engine.prototype.init = function () {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return true;
    }
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    // iOS: play through the silent switch like a music app would
    try { if (navigator.audioSession) navigator.audioSession.type = 'playback'; } catch (e) {}
    var ctx = (this.ctx = new AC());

    var comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.2;
    comp.connect(ctx.destination);

    var master = (this.master = ctx.createGain());
    master.gain.value = 0.85;
    master.connect(comp);

    // lead bus: the strings. LISTEN mutes this, like the instrument going quiet.
    var lead = (this.lead = ctx.createGain());
    lead.gain.value = 1;
    lead.connect(master);

    // synced echo
    var delay = (this.delay = ctx.createDelay(2));
    delay.delayTime.value = (60 / this.bpm) * 0.75;
    var fb = ctx.createGain();
    fb.gain.value = 0.34;
    var dlp = ctx.createBiquadFilter();
    dlp.type = 'lowpass';
    dlp.frequency.value = 2400;
    var dwet = ctx.createGain();
    dwet.gain.value = 0.3;
    lead.connect(delay);
    delay.connect(dlp);
    dlp.connect(fb);
    fb.connect(delay);
    dlp.connect(dwet);
    dwet.connect(master);

    // room
    var rev = ctx.createConvolver();
    rev.buffer = this.impulse(2.6, 3.2);
    var rsend = (this.rsend = ctx.createGain());
    rsend.gain.value = 0.28;
    rsend.connect(rev);
    rev.connect(master);
    lead.connect(rsend);

    // jam-row chords
    var pads = (this.pads = ctx.createGain());
    pads.gain.value = 0.9;
    pads.connect(master);
    pads.connect(rsend);

    // the song, and the ear that hears it
    var back = (this.back = ctx.createGain());
    back.gain.value = 0.62;
    back.connect(master);
    var an = (this.analyser = ctx.createAnalyser());
    an.fftSize = 8192;
    an.smoothingTimeConstant = 0;
    back.connect(an);

    this.noise = this.noiseBuffer();
    return true;
  };

  Engine.prototype.impulse = function (secs, decay) {
    var ctx = this.ctx, len = Math.floor(ctx.sampleRate * secs);
    var buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (var ch = 0; ch < 2; ch++) {
      var d = buf.getChannelData(ch);
      for (var i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return buf;
  };

  Engine.prototype.noiseBuffer = function () {
    var ctx = this.ctx, len = ctx.sampleRate;
    var buf = ctx.createBuffer(1, len, ctx.sampleRate);
    var d = buf.getChannelData(0);
    for (var i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  };

  Engine.prototype.setTempo = function (bpm) {
    this.bpm = bpm;
    if (this.ctx) this.delay.delayTime.setTargetAtTime((60 / bpm) * 0.75, now(this.ctx), 0.08);
  };

  function hold(param, t) {
    if (param.cancelAndHoldAtTime) param.cancelAndHoldAtTime(t);
    else { var v = param.value; param.cancelScheduledValues(t); param.setValueAtTime(v, t); }
  }

  /* One string: two detuned saws and a sub through a filter, with a slow
     vibrato that fades in on held notes. Glides by retargeting the pitch. */
  function Voice(eng, midi) {
    var ctx = eng.ctx, t = now(ctx), f = mtof(midi);
    this.eng = eng;
    this.midi = midi;
    this.from = midi;
    this.t0 = performance.now();

    var o1 = ctx.createOscillator(), o2 = ctx.createOscillator(), o3 = ctx.createOscillator();
    o1.type = 'sawtooth';
    o2.type = 'sawtooth';
    o3.type = 'sine';
    o2.detune.value = 9;
    o1.frequency.value = f;
    o2.frequency.value = f;
    o3.frequency.value = f / 2;
    var g1 = ctx.createGain(), g2 = ctx.createGain(), g3 = ctx.createGain();
    g1.gain.value = 0.5;
    g2.gain.value = 0.45;
    g3.gain.value = 0.4;

    var filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.Q.value = 2.2;
    filt.frequency.setValueAtTime(420, t);
    filt.frequency.linearRampToValueAtTime(3600, t + 0.035);
    filt.frequency.setTargetAtTime(1700, t + 0.04, 0.3);

    var amp = ctx.createGain();
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(0.2, t + 0.012);
    amp.gain.setTargetAtTime(0.15, t + 0.02, 0.4);

    var lfo = ctx.createOscillator(), vib = ctx.createGain();
    lfo.frequency.value = 5.3;
    vib.gain.setValueAtTime(0, t);
    vib.gain.setValueAtTime(0, t + 0.4);
    vib.gain.linearRampToValueAtTime(11, t + 1.1);
    lfo.connect(vib);
    vib.connect(o1.detune);
    vib.connect(o2.detune);

    o1.connect(g1); o2.connect(g2); o3.connect(g3);
    g1.connect(filt); g2.connect(filt); g3.connect(filt);
    filt.connect(amp);
    amp.connect(eng.lead);
    [o1, o2, o3, lfo].forEach(function (o) { o.start(t); });

    this.oscs = [o1, o2, o3, lfo];
    this.pitch = [o1, o2, o3];
    this.amp = amp;
    this.filt = filt;
    this.vib = vib;
  }

  Voice.prototype.glide = function (midi) {
    var ctx = this.eng.ctx, t = now(ctx), f = mtof(midi);
    this.from = this.current();
    this.midi = midi;
    this.t0 = performance.now();
    this.pitch[0].frequency.setTargetAtTime(f, t, GLIDE_TAU);
    this.pitch[1].frequency.setTargetAtTime(f, t, GLIDE_TAU);
    this.pitch[2].frequency.setTargetAtTime(f / 2, t, GLIDE_TAU);
    // a slide opens the filter a touch, the way a pluck of the string would
    hold(this.filt.frequency, t);
    this.filt.frequency.linearRampToValueAtTime(2600, t + 0.05);
    this.filt.frequency.setTargetAtTime(1700, t + 0.06, 0.35);
    hold(this.vib.gain, t);
    this.vib.gain.linearRampToValueAtTime(0, t + 0.03);
    this.vib.gain.setValueAtTime(0, t + 0.35);
    this.vib.gain.linearRampToValueAtTime(11, t + 1);
  };

  Voice.prototype.current = function () {
    var dt = (performance.now() - this.t0) / 1000;
    return this.midi + (this.from - this.midi) * Math.exp(-dt / GLIDE_TAU);
  };

  Voice.prototype.release = function () {
    var ctx = this.eng.ctx, t = now(ctx);
    hold(this.amp.gain, t);
    this.amp.gain.setTargetAtTime(0, t, 0.11);
    this.oscs.forEach(function (o) { try { o.stop(t + 0.9); } catch (e) {} });
    this.gone = performance.now();
  };

  /* A latched chord for the jam row: soft and wide, slides between chords. */
  function Pad(eng, notes) {
    var ctx = eng.ctx, t = now(ctx);
    this.eng = eng;
    var filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = 1300;
    filt.Q.value = 0.6;
    var amp = (this.amp = ctx.createGain());
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(0.13, t + 0.28);
    filt.connect(amp);
    amp.connect(eng.pads);
    this.oscs = [];
    this.notes = notes.slice();
    var self = this;
    notes.forEach(function (m, i) {
      ['triangle', 'sawtooth'].forEach(function (type, k) {
        var o = ctx.createOscillator(), g = ctx.createGain();
        o.type = type;
        o.frequency.value = mtof(m);
        o.detune.value = k ? -7 + i * 3 : 5;
        g.gain.value = k ? 0.16 : 0.34;
        o.connect(g);
        g.connect(filt);
        o.start(t);
        o._i = i;
        self.oscs.push(o);
      });
    });
  }
  Pad.prototype.glide = function (notes) {
    var t = now(this.eng.ctx);
    this.notes = notes.slice();
    this.oscs.forEach(function (o) { o.frequency.setTargetAtTime(mtof(notes[o._i]), t, 0.07); });
  };
  Pad.prototype.release = function () {
    var t = now(this.eng.ctx);
    hold(this.amp.gain, t);
    this.amp.gain.setTargetAtTime(0, t, 0.25);
    this.oscs.forEach(function (o) { try { o.stop(t + 1.6); } catch (e) {} });
  };

  /* ------------------------------------------------------------------- song */

  // i, VI, VII, i: tonic-heavy so it reads as its own key, not its relative
  var PROG = [{ r: 0, q: [0, 3, 7] }, { r: 8, q: [0, 4, 7] }, { r: 10, q: [0, 4, 7] }, { r: 0, q: [0, 3, 7] }];
  var ARP = [0, 1, 2, 1, 2, 3, 2, 1];

  function Song(eng, tonic, bpm) {
    this.eng = eng;
    this.tonic = tonic;
    this.bpm = bpm;
    this.base = 36 + tonic; // bass register, C2 up
    if (this.base > 43) this.base -= 12;
  }

  Song.prototype.start = function () {
    var self = this, ctx = this.eng.ctx;
    this.step = 0;
    this.next = now(ctx) + 0.08;
    this.out = ctx.createGain();
    this.out.gain.value = 1;
    this.out.connect(this.eng.back);
    this.timer = setInterval(function () { self.tick(); }, 25);
    this.tick();
  };

  Song.prototype.stop = function () {
    clearInterval(this.timer);
    var t = now(this.eng.ctx);
    this.out.gain.setTargetAtTime(0, t, 0.08);
    var out = this.out;
    setTimeout(function () { try { out.disconnect(); } catch (e) {} }, 1500);
  };

  Song.prototype.tick = function () {
    var ctx = this.eng.ctx, sp = 60 / this.bpm / 4;
    while (this.next < now(ctx) + 0.14) {
      this.play(this.step, this.next, sp);
      this.next += sp;
      this.step++;
    }
  };

  Song.prototype.play = function (step, t, sp) {
    // a chord every half bar, so the whole progression fits in one listen
    var s = step % 16, bar = Math.floor(step / 8) % 4, ch = PROG[bar];
    var root = this.base + ch.r;
    if (s === 0 || s === 8 || (s === 11 && bar % 2)) this.kick(t);
    if (s === 4 || s === 12) this.snare(t);
    if (s % 2 === 0) this.hat(t, s % 4 === 2 ? 0.1 : 0.05);
    if ([0, 3, 6, 8, 11, 14].indexOf(s) > -1) this.bass(root + (s === 6 || s === 14 ? 12 : 0), t, sp * 1.6);
    if (s % 8 === 0) this.chord(ch.q.map(function (x) { return root + 24 + x; }), t, sp * 7.6);
    if (s % 2 === 0) {
      var tones = ch.q.concat([12]);
      this.pluck(root + 36 + tones[ARP[(s / 2) % 8]], t, 0.045);
    }
  };

  Song.prototype.kick = function (t) {
    var ctx = this.eng.ctx, o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.12);
    g.gain.setValueAtTime(0.95, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.34);
    o.connect(g); g.connect(this.out);
    o.start(t); o.stop(t + 0.36);
  };
  Song.prototype.snare = function (t) {
    var ctx = this.eng.ctx, n = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    n.buffer = this.eng.noise;
    f.type = 'bandpass'; f.frequency.value = 2200; f.Q.value = 0.8;
    g.gain.setValueAtTime(0.32, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.17);
    n.connect(f); f.connect(g); g.connect(this.out);
    n.start(t, Math.random() * 0.5); n.stop(t + 0.2);
  };
  Song.prototype.hat = function (t, v) {
    var ctx = this.eng.ctx, n = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    n.buffer = this.eng.noise;
    f.type = 'highpass'; f.frequency.value = 7500;
    g.gain.setValueAtTime(v, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.045);
    n.connect(f); f.connect(g); g.connect(this.out);
    n.start(t, Math.random() * 0.5); n.stop(t + 0.06);
  };
  Song.prototype.bass = function (m, t, dur) {
    var ctx = this.eng.ctx, o = ctx.createOscillator(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = mtof(m);
    f.type = 'lowpass'; f.Q.value = 3;
    f.frequency.setValueAtTime(900, t);
    f.frequency.exponentialRampToValueAtTime(180, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(f); f.connect(g); g.connect(this.out);
    o.start(t); o.stop(t + dur + 0.02);
  };
  Song.prototype.chord = function (notes, t, dur) {
    var ctx = this.eng.ctx, out = this.out, f = ctx.createBiquadFilter(), g = ctx.createGain();
    f.type = 'lowpass'; f.frequency.value = 1500;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.075, t + 0.06);
    g.gain.setValueAtTime(0.075, t + dur - 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    f.connect(g); g.connect(out); g.connect(this.eng.rsend);
    notes.forEach(function (m, i) {
      var o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = mtof(m);
      o.detune.value = (i - 1) * 6;
      o.connect(f);
      o.start(t); o.stop(t + dur + 0.02);
    });
  };
  Song.prototype.pluck = function (m, t, v) {
    var ctx = this.eng.ctx, o = ctx.createOscillator(), g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = mtof(m);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(v, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    o.connect(g); g.connect(this.out); g.connect(this.eng.rsend);
    o.start(t); o.stop(t + 0.3);
  };

  /* ------------------------------------------------------------ key finding */

  function correlate(a, b) {
    var n = a.length, ma = 0, mb = 0, i;
    for (i = 0; i < n; i++) { ma += a[i]; mb += b[i]; }
    ma /= n; mb /= n;
    var num = 0, da = 0, db = 0;
    for (i = 0; i < n; i++) {
      num += (a[i] - ma) * (b[i] - mb);
      da += (a[i] - ma) * (a[i] - ma);
      db += (b[i] - mb) * (b[i] - mb);
    }
    return da && db ? num / Math.sqrt(da * db) : 0;
  }

  function findKey(chroma) {
    var best = null, second = -2;
    for (var tonic = 0; tonic < 12; tonic++) {
      var rot = [];
      for (var i = 0; i < 12; i++) rot.push(chroma[(i + tonic) % 12]);
      [['major', KK_MAJOR], ['minor', KK_MINOR]].forEach(function (p) {
        var r = correlate(rot, p[1]);
        if (!best || r > best.r) {
          if (best) second = Math.max(second, best.r);
          best = { tonic: tonic, mode: p[0], r: r };
        } else if (r > second) second = r;
      });
    }
    best.margin = best.r - second;
    return best;
  }

  /* ------------------------------------------------------------- the widget */

  function Playground(root) {
    this.root = root;
    this.mode = root.getAttribute('data-mode') || 'free';
    this.eng = new Engine();
    this.key = 9; // A
    this.scale = 'minpent';
    this.lockOn = true;
    this.strings = [{ held: [], voice: null }, { held: [], voice: null }, { held: [], voice: null }];
    this.pad = null;
    this.padIdx = -1;
    this.pointers = {};
    this.song = null;
    this.songKey = this.mode === 'autokey' ? SONG_KEYS[Math.floor(Math.random() * SONG_KEYS.length)] : 9;
    this.songBpm = this.mode === 'autokey' ? 88 + Math.floor(Math.random() * 5) * 4 : 90;
    this.listening = false;
    this.clash = 0;
    this.trail = [];
    this.inView = false;

    this.$ = function (sel) { return root.querySelector(sel); };
    this.keysEl = this.$('[data-keys]');
    this.jamEl = this.$('[data-jam]');
    this.canvas = this.$('canvas');
    this.build();
    this.bind();
    this.status();
  }

  Playground.prototype.base = function () {
    // the root nearest A3, so retuning never throws the keys an octave away
    return 57 + ((((this.key - 9) % 12) + 18) % 12) - 6;
  };

  Playground.prototype.noteAt = function (r, c) {
    var sc = SCALES[this.lockOn ? this.scale : 'chromatic'];
    if (!this.lockOn) return this.base() + c + r * 5; // plain fourths, every semitone
    var n = sc.steps.length, off = n === 5 ? 2 : 3, deg = c + r * off;
    return this.base() + sc.steps[deg % n] + 12 * Math.floor(deg / n);
  };

  Playground.prototype.chordAt = function (k) {
    var p = SCALES[this.scale].parent, b = this.base();
    var deg = [k, k + 2, k + 4];
    var notes = deg.map(function (d) { return b + p[d % 7] + 12 * Math.floor(d / 7); });
    var third = notes[1] - notes[0], fifth = notes[2] - notes[0];
    var name = pcName(notes[0]) + (third === 3 ? (fifth === 6 ? '°' : 'm') : '');
    return { notes: notes, name: name };
  };

  // the pitch classes the song (or the beat) is actually in
  Playground.prototype.songScale = function () {
    var k = this.songKey;
    return MINOR.map(function (x) { return (x + k) % 12; });
  };

  Playground.prototype.build = function () {
    var html = '';
    for (var r = 2; r >= 0; r--) {
      html += '<div class="lpp-row" data-row="' + r + '"><span class="lpp-rowlab">string ' + (r + 1) + '</span>';
      for (var c = 0; c < 10; c++) {
        html += '<button type="button" class="lpp-key c' + c + '" data-key="' + r + ',' + c + '" tabindex="-1">' +
          '<b></b><i>' + ROWS[r].label[c] + '</i></button>';
      }
      html += '</div>';
    }
    this.keysEl.innerHTML = html;
    var jam = '<span class="lpp-rowlab">jam row</span>';
    for (var j = 0; j < 10; j++) {
      jam += '<button type="button" class="lpp-pad c' + j + '" data-pad="' + j + '" aria-pressed="false"><b></b><i>' + JAM.label[j] + '</i></button>';
    }
    if (this.jamEl) this.jamEl.innerHTML = jam;
    this.label(false);
  };

  Playground.prototype.label = function (animate) {
    var self = this;
    var keys = this.keysEl.querySelectorAll('[data-key]');
    Array.prototype.forEach.call(keys, function (k, i) {
      var rc = k.getAttribute('data-key').split(','), m = self.noteAt(+rc[0], +rc[1]);
      var set = function () {
        k.querySelector('b').textContent = pcName(m);
        k.setAttribute('aria-label', noteName(m));
        k.classList.toggle('is-root', ((m - self.key) % 12 + 12) % 12 === 0 && self.lockOn);
      };
      if (animate) {
        k.style.setProperty('--d', (+rc[1] * 35 + (2 - rc[0]) * 60) + 'ms');
        k.classList.remove('is-flip');
        void k.offsetWidth;
        k.classList.add('is-flip');
        setTimeout(set, 160 + +rc[1] * 35 + (2 - rc[0]) * 60);
      } else set();
    });
    if (this.jamEl) {
      Array.prototype.forEach.call(this.jamEl.querySelectorAll('[data-pad]'), function (p) {
        p.querySelector('b').textContent = self.chordAt(+p.getAttribute('data-pad')).name;
      });
    }
  };

  Playground.prototype.status = function () {
    var sc = this.lockOn ? SCALES[this.scale].name : SCALES.chromatic.name;
    this.set('scale', this.lockOn ? pcName(this.key) + ' ' + sc : sc);
    this.set('clash', String(this.clash));
    var steps = this.root.querySelectorAll('[data-step]');
    var stage = this.listening ? 3 : this.locked ? 4 : this.song ? 2 : 1;
    Array.prototype.forEach.call(steps, function (s) {
      var n = +s.getAttribute('data-step');
      s.classList.toggle('is-done', n < stage);
      s.classList.toggle('is-now', n === stage || (stage === 4 && n === 3));
    });
  };

  Playground.prototype.set = function (k, v) {
    var el = this.root.querySelector('[data-k="' + k + '"]');
    if (el) el.textContent = v;
  };

  Playground.prototype.hint = function (text) {
    var el = this.$('[data-hint]');
    if (el) el.textContent = text;
  };

  Playground.prototype.wake = function () {
    if (!this.eng.init()) {
      this.hint('This browser has no Web Audio, so the demo cannot make sound here.');
      return false;
    }
    this.root.classList.add('is-live');
    this.loop();
    return true;
  };

  /* strings */
  Playground.prototype.press = function (id) {
    if (!this.wake()) return;
    var rc = id.split(','), r = +rc[0], c = +rc[1], s = this.strings[r];
    var m = this.noteAt(r, c);
    var i = s.held.indexOf(id);
    if (i > -1) s.held.splice(i, 1);
    s.held.push(id);
    if (s.voice && !s.voice.gone) s.voice.glide(m);
    else s.voice = new Voice(this.eng, m);
    this.mark(id, true, m);
    this.set('note', noteName(m));
  };

  Playground.prototype.lift = function (id) {
    var r = +id.split(',')[0], s = this.strings[r];
    var i = s.held.indexOf(id);
    if (i === -1) return;
    s.held.splice(i, 1);
    this.mark(id, false);
    if (!s.voice) return;
    if (s.held.length) {
      var last = s.held[s.held.length - 1].split(',');
      s.voice.glide(this.noteAt(+last[0], +last[1]));
    } else {
      s.voice.release();
      s.voice = null;
    }
  };

  Playground.prototype.mark = function (id, on, m) {
    var k = this.keysEl.querySelector('[data-key="' + id + '"]');
    if (!k) return;
    k.classList.toggle('is-on', on);
    if (!on) { k.classList.remove('is-bad'); return; }
    // does this note belong in what's playing? only meaningful with a song on
    var bad = false;
    if (this.song && !this.listening) {
      bad = this.songScale().indexOf(((m % 12) + 12) % 12) === -1;
      if (bad) { this.clash++; this.status(); }
    }
    k.classList.toggle('is-bad', bad);
  };

  /* jam row */
  Playground.prototype.tapPad = function (idx) {
    if (!this.wake()) return;
    var pads = this.jamEl.querySelectorAll('[data-pad]');
    if (this.pad && this.padIdx === idx) {
      this.pad.release();
      this.pad = null;
      this.padIdx = -1;
    } else {
      var ch = this.chordAt(idx);
      if (this.pad) this.pad.glide(ch.notes);
      else this.pad = new Pad(this.eng, ch.notes);
      this.padIdx = idx;
      this.set('note', ch.name);
    }
    var self = this;
    Array.prototype.forEach.call(pads, function (p) {
      var on = +p.getAttribute('data-pad') === self.padIdx;
      p.classList.toggle('is-on', on);
      p.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Playground.prototype.retune = function (tonic, scale) {
    this.key = tonic;
    this.scale = scale;
    this.label(true);
    // held strings slide to wherever their key now points
    var self = this;
    this.strings.forEach(function (s) {
      if (s.voice && s.held.length) {
        var rc = s.held[s.held.length - 1].split(',');
        s.voice.glide(self.noteAt(+rc[0], +rc[1]));
      }
    });
    if (this.pad) this.pad.glide(this.chordAt(this.padIdx).notes);
    this.status();
  };

  /* the song */
  Playground.prototype.toggleSong = function (fresh) {
    if (!this.wake()) return;
    if (this.song && !fresh) {
      this.song.stop();
      this.song = null;
      this.songBtn(false);
      this.status();
      this.hint(this.mode === 'autokey' ? 'Song stopped. Put one on to try again.' : 'Beat stopped. The keys still play.');
      return;
    }
    if (this.song) this.song.stop();
    if (fresh) {
      var pool = SONG_KEYS.filter(function (k) { return k !== this.songKey; }, this);
      this.songKey = pool[Math.floor(Math.random() * pool.length)];
      this.songBpm = 84 + Math.floor(Math.random() * 7) * 4;
    }
    this.song = new Song(this.eng, this.songKey, this.songBpm);
    this.song.start();
    this.songBtn(true);
    this.clash = 0;
    this.status();
    if (this.mode === 'autokey') {
      this.hint(this.locked
        ? 'A new song, in a new key. Your keys are still tuned to the last one. Play a little, then hit Listen.'
        : 'Now play along. Some notes will fight the song, the red ones. That is what Listen fixes.');
    } else {
      this.hint('The beat is in A minor, so is every key. Play anything.');
    }
  };

  Playground.prototype.songBtn = function (on) {
    var b = this.$('[data-act="song"]');
    if (!b) return;
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    var lab = b.querySelector('span');
    if (lab) lab.textContent = on ? b.getAttribute('data-off') : b.getAttribute('data-on');
    var nb = this.$('[data-act="newsong"]');
    if (nb) nb.hidden = !on;
  };

  Playground.prototype.listen = function () {
    if (this.listening || !this.wake()) return;
    var self = this, eng = this.eng, ctx = eng.ctx, an = eng.analyser;
    var bins = new Float32Array(an.frequencyBinCount), hz = ctx.sampleRate / an.fftSize;
    var chroma = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], energy = 0, frames = 0;
    var bars = this.root.querySelectorAll('[data-chroma] span');
    var card = this.$('[data-card]');
    this.listening = true;
    this.root.classList.add('is-listening');
    if (card) card.hidden = true;
    // the instrument goes quiet while it listens
    eng.lead.gain.setTargetAtTime(0, now(ctx), 0.05);
    eng.pads.gain.setTargetAtTime(0, now(ctx), 0.05);
    this.set('state', 'LISTENING…');
    this.hint('Listening. The synth goes quiet so it only hears the song.');
    this.status();
    var start = performance.now(), DUR = 5600; // the instrument hears at least five seconds too

    (function frame() {
      an.getFloatFrequencyData(bins);
      // Only spectral peaks well above this frame's floor count, and only
      // up to ~1 kHz, where fundamentals live. Overtones of every note sit
      // on its fifth and major third, and counting them drags minor songs
      // toward major keys.
      var lo = Math.floor(65 / hz), hi = Math.min(bins.length - 1, Math.ceil(1000 / hz));
      var mags = [], i;
      for (i = lo - 1; i <= hi + 1; i++) mags[i] = isFinite(bins[i]) ? Math.pow(10, bins[i] / 20) : 0;
      var sorted = mags.slice(lo, hi + 1).sort(function (a, b) { return a - b; });
      var floor = sorted[Math.floor(sorted.length / 2)] || 0;
      for (i = lo; i <= hi; i++) {
        var mag = mags[i];
        if (mag < floor * 6 || mag < mags[i - 1] || mag < mags[i + 1]) continue;
        var midi = 69 + 12 * Math.log2((i * hz) / 440), near = Math.round(midi);
        if (Math.abs(midi - near) > 0.35) continue;
        chroma[((near % 12) + 12) % 12] += Math.sqrt(mag - floor);
        energy += mag;
      }
      frames++;
      var max = Math.max.apply(null, chroma) || 1;
      Array.prototype.forEach.call(bars, function (b, i) { b.style.transform = 'scaleY(' + Math.max(0.06, chroma[i] / max).toFixed(3) + ')'; });
      if (performance.now() - start < DUR) requestAnimationFrame(frame);
      else self.verdict(chroma, energy / Math.max(1, frames));
    })();
  };

  Playground.prototype.verdict = function (chroma, level) {
    var eng = this.eng, t = now(eng.ctx), card = this.$('[data-card]');
    this.listening = false;
    this.root.classList.remove('is-listening');
    eng.lead.gain.setTargetAtTime(1, t, 0.08);
    eng.pads.gain.setTargetAtTime(0.9, t, 0.08);

    var text, sub;
    if (!this.song || level < 0.02) {
      text = 'NO SIGNAL';
      sub = 'Nothing to hear, so nothing changed. Put a song on first.';
      this.set('state', 'NO SIGNAL');
      this.hint('It heard a quiet room and changed nothing, same as the real one. Put a song on, then Listen.');
    } else {
      var k = findKey(chroma);
      this.locked = true;
      this.retune(k.tonic, k.mode === 'major' ? 'major' : 'minor');
      eng.setTempo(this.song.bpm);
      text = pcName(k.tonic) + ' ' + (k.mode === 'major' ? 'MAJOR' : 'MINOR') + ' · ' + this.song.bpm + ' BPM ✓';
      sub = 'Keys retuned, echo locked to the beat. Same keys under your fingers, all of them in the song now.';
      this.set('state', 'LOCKED');
      this.clash = 0;
      this.hint('Play again. Same keys, same shapes. Nothing clashes now.');
    }
    if (card) {
      card.querySelector('[data-card-t]').textContent = text;
      card.querySelector('[data-card-s]').textContent = sub;
      card.hidden = false;
      clearTimeout(this.cardT);
      this.cardT = setTimeout(function () { card.hidden = true; }, 6500);
    }
    this.status();
  };

  Playground.prototype.toggleLock = function () {
    this.lockOn = !this.lockOn;
    var b = this.$('[data-act="lock"]');
    if (b) {
      b.setAttribute('aria-pressed', this.lockOn ? 'true' : 'false');
      b.querySelector('span').textContent = this.lockOn ? 'Scale lock: on' : 'Scale lock: off';
    }
    this.root.classList.toggle('is-unlocked', !this.lockOn);
    this.label(true);
    this.status();
    this.hint(this.lockOn
      ? 'Scale lock is back on. Every key fits again.'
      : 'This is every note, like a piano. Play a while with the beat on and count the red ones.');
  };

  /* pitch trail, like the instrument's own display */
  Playground.prototype.loop = function () {
    if (this.raf) return;
    var self = this;
    var tick = function () {
      self.raf = requestAnimationFrame(tick);
      if (self.inView) self.draw();
    };
    this.raf = requestAnimationFrame(tick);
  };

  Playground.prototype.draw = function () {
    var cv = this.canvas;
    if (!cv) return;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var w = cv.clientWidth, h = cv.clientHeight;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
    }
    var g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);

    var sample = this.strings.map(function (s) { return s.voice && !s.voice.gone ? s.voice.current() : null; });
    this.trail.push(sample);
    var cols = Math.ceil(w / 2);
    while (this.trail.length > cols) this.trail.shift();

    var lo = this.base() - 5, hi = this.base() + 31;
    var y = function (m) { return h - ((m - lo) / (hi - lo)) * h; };

    // root notes as fret markers
    g.strokeStyle = 'rgba(233,239,231,0.09)';
    g.setLineDash([3, 5]);
    g.lineWidth = 1;
    for (var m = this.base(); m <= hi; m += 12) {
      g.beginPath(); g.moveTo(0, y(m)); g.lineTo(w, y(m)); g.stroke();
    }
    g.setLineDash([]);

    var colors = ['#57e389', '#4cc3d9', '#f5a83c'];
    var x0 = w - this.trail.length * 2;
    for (var r = 0; r < 3; r++) {
      g.beginPath();
      var pen = false;
      for (var i = 0; i < this.trail.length; i++) {
        var v = this.trail[i][r];
        if (v == null) { pen = false; continue; }
        var X = x0 + i * 2, Y = y(v);
        if (!pen) { g.moveTo(X, Y); pen = true; } else g.lineTo(X, Y);
      }
      g.strokeStyle = colors[r];
      g.lineWidth = 2.2;
      g.shadowColor = colors[r];
      g.shadowBlur = 8;
      g.stroke();
      g.shadowBlur = 0;
    }
  };

  Playground.prototype.bind = function () {
    var self = this, root = this.root;

    // pointer: press, drag across keys to slide, lift
    var keyUnder = function (x, y) {
      var el = document.elementFromPoint(x, y);
      var k = el && el.closest ? el.closest('[data-key]') : null;
      return k && root.contains(k) ? k.getAttribute('data-key') : null;
    };
    this.keysEl.addEventListener('pointerdown', function (e) {
      var k = e.target.closest('[data-key]');
      if (!k) return;
      e.preventDefault();
      var id = k.getAttribute('data-key');
      self.pointers[e.pointerId] = id;
      self.press(id);
    });
    window.addEventListener('pointermove', function (e) {
      if (!(e.pointerId in self.pointers)) return;
      var cur = self.pointers[e.pointerId], id = keyUnder(e.clientX, e.clientY);
      if (!id || id === cur) return;
      self.pointers[e.pointerId] = id;
      self.press(id); // press first, so a same-row move glides instead of retriggering
      if (cur) self.lift(cur);
    });
    var up = function (e) {
      if (!(e.pointerId in self.pointers)) return;
      var cur = self.pointers[e.pointerId];
      delete self.pointers[e.pointerId];
      if (cur) self.lift(cur);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    this.keysEl.addEventListener('contextmenu', function (e) { e.preventDefault(); });

    if (this.jamEl) {
      this.jamEl.addEventListener('click', function (e) {
        var p = e.target.closest('[data-pad]');
        if (p) self.tapPad(+p.getAttribute('data-pad'));
      });
    }

    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b) return;
      var a = b.getAttribute('data-act');
      if (a === 'song') self.toggleSong(false);
      else if (a === 'newsong') self.toggleSong(true);
      else if (a === 'listen') self.listen();
      else if (a === 'lock') self.toggleLock();
    });

    // the computer keyboard is laid out like GLIDE's; live only while in view
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        self.inView = es[0].isIntersecting;
        if (!self.inView) self.releaseAll();
      }, { threshold: 0.25 }).observe(root);
    } else this.inView = true;

    var codeMap = {};
    ROWS.forEach(function (row, r) { row.codes.forEach(function (c, i) { codeMap[c] = r + ',' + i; }); });
    var jamMap = {};
    JAM.codes.forEach(function (c, i) { jamMap[c] = i; });
    var typing = function (e) {
      var t = e.target;
      return t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    };
    window.addEventListener('keydown', function (e) {
      if (!self.inView || e.metaKey || e.ctrlKey || e.altKey || typing(e)) return;
      if (e.code in codeMap) {
        e.preventDefault();
        if (!e.repeat) self.press(codeMap[e.code]);
      } else if (e.code in jamMap && self.jamEl) {
        e.preventDefault();
        if (!e.repeat) self.tapPad(jamMap[e.code]);
      }
    });
    window.addEventListener('keyup', function (e) {
      if (e.code in codeMap) self.lift(codeMap[e.code]);
    });
    window.addEventListener('blur', function () { self.releaseAll(); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.releaseAll();
        if (self.song) self.toggleSong(false);
      }
    });
  };

  Playground.prototype.releaseAll = function () {
    var self = this;
    this.pointers = {};
    this.strings.forEach(function (s) {
      s.held.slice().forEach(function (id) { self.lift(id); });
    });
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-lp-play]'), function (el) {
      if (!el._lpp) el._lpp = new Playground(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
